/**
 * YOURDRIVE INTEGRATION SERVICE
 * ─────────────────────────────────────────────────────────────────────────────────
 * This service embeds the YourDrive document management app inside an <iframe>
 * and silently SSOs the currently logged-in Firebase user into it.
 *
 * TWO integration methods are provided:
 *   A) URL-param SSO  → easiest; works on iframe first-load
 *   B) postMessage SSO → best for dynamic token refresh inside a long-lived iframe
 */

import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/**
 * Port Mapping:
 * - MySpace (AccessLife): localhost:4201 (Parent window)
 * - YourDrive Frontend: localhost:4200 (Runs in iframe)
 * - YourDrive Backend: localhost:5000 (API)
 */
const YOURDRIVE_URL = 'https://drive.accesslife.org'; // YourDrive frontend URL

@Injectable({ providedIn: 'root' })
export class YourDriveIntegrationService {

  constructor(
    private afAuth: AngularFireAuth,
    private sanitizer: DomSanitizer
  ) {}

  // ─────────────────────────────────────────────────────────────
  // Method A: build an iframe src URL with the token embedded
  // Use this when first creating the <iframe> element.
  //
  // In your component template:
  //   <iframe [src]="iframeSrc" width="100%" height="100%" frameborder="0"></iframe>
  //
  // In your component class:
  //   iframeSrc: SafeResourceUrl;
  //   async ngOnInit() {
  //     this.iframeSrc = await this.yourDriveService.buildIframeSrc();
  //   }
  // ─────────────────────────────────────────────────────────────
  async buildIframeSrc(): Promise<SafeResourceUrl> {
    const token = await this.getFirebaseToken();
    const url = `${YOURDRIVE_URL}/sso?token=${encodeURIComponent(token)}`;
    console.log('🔗 Building YourDrive iframe URL with SSO token');
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // ─────────────────────────────────────────────────────────────
  // Method B: send the token to an already-loaded iframe via postMessage
  // Use this when the iframe is already on screen (e.g. tab switch,
  // or to refresh after a token rotation).
  //
  // In your component:
  //   @ViewChild('yourDriveFrame') iframeRef!: ElementRef<HTMLIFrameElement>;
  //   async onTabActivated() {
  //     await this.yourDriveService.postMessageSSO(this.iframeRef.nativeElement);
  //   }
  // ─────────────────────────────────────────────────────────────
  async postMessageSSO(iframe: HTMLIFrameElement): Promise<void> {
    try {
      const token = await this.getFirebaseToken();
      console.log('📤 Sending SSO token to YourDrive via postMessage');

      iframe.contentWindow?.postMessage(
        {
          type: 'YOURDRIVE_SSO',
          firebaseToken: token,
          device: 'Accesslife MySpace'
        },
        YOURDRIVE_URL  // targetOrigin (security: only send to your YourDrive origin)
      );
    } catch (error) {
      console.error('❌ Failed to send SSO token:', error);
      throw error;
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Listen for SSO result messages from YourDrive (optional)
  // Call this in your component ngOnInit and unsubscribe on destroy.
  // ─────────────────────────────────────────────────────────────
  listenForSsoResult(
    onSuccess?: (userId: string) => void,
    onFailed?: (error: string) => void
  ): () => void {
    const handler = (event: MessageEvent) => {
      // Security check: only accept messages from YourDrive origin
      if (event.origin !== YOURDRIVE_URL) {
        return;
      }

      if (event.data?.type === 'YOURDRIVE_SSO_SUCCESS') {
        console.log('✅ YourDrive SSO successful:', event.data.userId);
        onSuccess?.(event.data.userId);
      }

      if (event.data?.type === 'YOURDRIVE_SSO_FAILED') {
        console.error('❌ YourDrive SSO failed:', event.data.error);
        onFailed?.(event.data.error);
      }
    };

    window.addEventListener('message', handler);
    console.log('👂 Listening for YourDrive SSO messages');

    // Return cleanup function
    return () => {
      window.removeEventListener('message', handler);
      console.log('🔇 Stopped listening for YourDrive SSO messages');
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Internal helper — get a fresh Firebase ID token for current user
  // ─────────────────────────────────────────────────────────────
  private getFirebaseToken(): Promise<string> {
    return new Promise((resolve, reject) => {
      this.afAuth.authState.subscribe(async (user) => {
        if (!user) {
          reject(new Error('No authenticated Firebase user found.'));
          return;
        }
        try {
          const token = await user.getIdToken(true);
          console.log('🔑 Firebase token obtained (length:', token?.length, ')');
          resolve(token);
        } catch (error) {
          console.error('❌ Failed to get Firebase token:', error);
          reject(error);
        }
      });
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Get current Firebase user
  // ─────────────────────────────────────────────────────────────
  async getCurrentUser() {
    return await this.afAuth.currentUser;
  }

  // ─────────────────────────────────────────────────────────────
  // Check if user is authenticated
  // Waits for Firebase to fully restore auth state after page refresh
  // ─────────────────────────────────────────────────────────────
  isAuthenticated(): Promise<boolean> {
    return new Promise((resolve) => {
      this.afAuth.authState.subscribe(user => {
        resolve(!!user);
      });
    });
  }
}

